/* ═══════════════════════════════════════════════════════════════════════════
   FF Cart Drawer — Scroll shadows, note shadow, blur class mirroring,
   mobile animation fix, bidirectional cart/drawer sync
   Loaded by sections/cart-drawer.liquid (defer)

   CRITICAL: This file loads with defer, AFTER pubsub.js and constants.js.
   Dawn's subscribe(), PUB_SUB_EVENTS, and routes are available here.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Blur class mirroring ─────────────────────────────────────────── */

  function initBlurMirror() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var html = document.documentElement;

    var observer = new MutationObserver(function () {
      if (drawer.classList.contains('active')) {
        html.classList.add('ff-cart-open');
      } else {
        html.classList.remove('ff-cart-open');
      }
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── Scrollbar shift fix ──────────────────────────────────────────── */

  function initScrollbarFix() {
    document.documentElement.style.scrollbarGutter = 'stable';
  }

  /* ── Scroll shadows + note shadow ─────────────────────────────────── */

  function initScrollShadows() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var inner = drawer.querySelector('.ff-cd');
    if (!inner) return;

    var shadowTop = inner.querySelector('.ff-cd__shadow-top');
    var shadowBottom = inner.querySelector('.ff-cd__shadow-bottom');
    if (!shadowTop || !shadowBottom) return;

    var scrollArea = inner.querySelector('cart-drawer-items');
    if (!scrollArea) return;

    var noteBody = inner.querySelector('.ff-cd__note-body');

    function updateShadows() {
      var st = scrollArea.scrollTop;
      var sh = scrollArea.scrollHeight;
      var ch = scrollArea.clientHeight;
      var sb = sh - st - ch;
      var isScrollable = sh > ch + 2;

      var showTop = isScrollable && st > 2;
      var showBottom = isScrollable && sb > 2;

      if (showTop) {
        shadowTop.classList.add('has-scroll-shadow');
      } else {
        shadowTop.classList.remove('has-scroll-shadow');
      }

      if (showBottom) {
        shadowBottom.classList.add('has-scroll-shadow');
      } else {
        shadowBottom.classList.remove('has-scroll-shadow');
      }

      if (noteBody) {
        if (showBottom) {
          noteBody.classList.add('ff-cd__note-body--shadow');
        } else {
          noteBody.classList.remove('ff-cd__note-body--shadow');
        }
      }
    }

    if (scrollArea._ffCdShadowBound) {
      updateShadows();
      return;
    }
    scrollArea._ffCdShadowBound = true;

    scrollArea.addEventListener('scroll', updateShadows, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(updateShadows);
      ro.observe(scrollArea);
    }

    var noteDetails = inner.querySelector('.ff-cd__note-details');
    if (noteDetails) {
      noteDetails.addEventListener('toggle', function () {
        noteBody = inner.querySelector('.ff-cd__note-body');
        updateShadows();
      });
    }

    updateShadows();

    drawer.addEventListener('transitionend', function () {
      if (drawer.classList.contains('active')) {
        updateShadows();
      }
    });
  }

  /* ── MutationObserver for Section Rendering API replacements ──────── */

  function observeDrawerUpdates() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var target = drawer.querySelector('#CartDrawer') || drawer;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length) {
          clearTimeout(observer._ffTimer);
          observer._ffTimer = setTimeout(initScrollShadows, 50);
          break;
        }
      }
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  /* ── Bidirectional cart page ↔ drawer sync ─────────────────────────────
     Dawn's CartItems and CartDrawerItems each self-update, then publish
     cartUpdate with source:'cart-items'. Both SKIP that source in their
     connectedCallback subscriber. So neither refreshes the other.

     This subscriber syncs BOTH sides on every cartUpdate. The side that
     just self-updated gets a redundant refresh (identical content, no
     visible flicker). The other side gets the needed sync. This avoids
     origin detection entirely — no race conditions from closing the
     drawer mid-update.

     CRITICAL for cart page: Dawn's onCartUpdate() fetches with
     section_id=main-cart-items (the FILENAME), which renders with
     schema DEFAULTS. Settings like cross-sell (default: false) get
     reset. We use the TEMPLATE-SPECIFIC section ID from dataset.id
     and replace only .js-contents, matching updateQuantity's own
     getSectionsToRender pattern.

     For drawer: onCartUpdate() uses section_id=cart-drawer which is
     correct (not template-specific).                                  */

  function initCartSync() {
    var cartItemsEl = document.querySelector('cart-items');
    if (!cartItemsEl) return;

    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event.source !== 'cart-items') return;

      /* ── Sync CART PAGE: template-specific ID + .js-contents ────── */
      var mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        var sectionId = mainCartItems.dataset.id;
        var tgt = mainCartItems.querySelector('.js-contents');

        if (sectionId && tgt) {
          /* Suppress stale error flash during sync */
          cartItemsEl.classList.add('ff-cp-syncing');

          fetch(routes.cart_url + '?section_id=' + sectionId)
            .then(function (r) { return r.text(); })
            .then(function (text) {
              var doc = new DOMParser().parseFromString(text, 'text/html');
              var src = doc.querySelector('#main-cart-items .js-contents')
                    || doc.querySelector('.js-contents');
              if (src) tgt.innerHTML = src.innerHTML;

              /* Toggle is-empty on cart-items and main-cart-footer */
              var isEmpty = event.cartData && event.cartData.item_count === 0;
              cartItemsEl.classList.toggle('is-empty', isEmpty);
              var footer = document.getElementById('main-cart-footer');
              if (footer) footer.classList.toggle('is-empty', isEmpty);
            })
            .catch(function () {})
            .finally(function () {
              cartItemsEl.classList.remove('ff-cp-syncing');
            });
        }
      }

      /* ── Sync DRAWER: Dawn's own onCartUpdate (correct pattern) ─── */
      var drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
        drawerItems.onCartUpdate();
      }
    });
  }

  /* ── Init ─────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initBlurMirror();
      initScrollShadows();
      observeDrawerUpdates();
      initScrollbarFix();
      initCartSync();
    });
  } else {
    initBlurMirror();
    initScrollShadows();
    observeDrawerUpdates();
    initScrollbarFix();
    initCartSync();
  }
})();